/** Lista de Mapas Unik do cliente — CRUD com suporte ao Motor Astral. Fase 3 (docs/00E). */
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router";
import { LayoutTemplate, Network, GitFork, List as ListIcon } from "lucide-react";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card, EmptyState, Skeleton } from "../../ui/Card";
import { Input } from "../../ui/Input";
import { AstralTemplatesModal } from "../../components/astral/AstralTemplatesModal";
import type { AstralTemplate } from "../../data/astralTemplates";

interface MapSummary {
  id: string;
  title: string;
  layout?: string;
  theme?: string;
  is_favorite?: boolean;
  nodeCount?: number;
  shared: boolean;
  updated_at?: string;
  updatedAt?: string;
}

export function Mapas() {
  const { id } = useParams();
  const [maps, setMaps] = useState<MapSummary[] | null>(null);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);

  async function load() {
    setMaps(await api.get<MapSummary[]>(`/clients/${id}/maps`).catch(() => []));
  }
  useEffect(() => {
    void load();
  }, [id]);

  async function criar(e: FormEvent) {
    e.preventDefault();
    if (title.trim() === "") return;
    setCreating(true);
    try {
      await api.post(`/clients/${id}/maps`, { title: title.trim() });
      setTitle("");
      await load();
    } finally {
      setCreating(false);
    }
  }

  async function handleSelectTemplate(template: AstralTemplate) {
    setIsTemplatesOpen(false);
    try {
      await api.post(`/maps`, {
        title: template.title,
        clientId: id,
        layout: template.data.layout,
        theme: template.data.theme,
        data_json: template.data,
      });
      await load();
    } catch {
      // ignore
    }
  }

  async function salvarRenome(mapId: string) {
    if (renameValue.trim() === "") return setRenamingId(null);
    await api.patch(`/clients/${id}/maps/${mapId}`, { title: renameValue.trim() });
    setRenamingId(null);
    await load();
  }

  async function excluir(mapId: string) {
    if (!window.confirm("Mover este mapa para a lixeira?")) return;
    await api.del(`/clients/${id}/maps/${mapId}`).catch(() => undefined);
    await load();
  }

  return (
    <AdminShell>
      <Link to={`/clientes/${id}`} className="type-caption text-text-tertiary">← Voltar ao cliente</Link>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mt-2">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="type-h1 text-text-cream">Mapas Unik</h1>
            <span className="rounded-full bg-indigo-500/15 border border-indigo-500/30 px-2 py-0.5 text-[11px] font-bold text-indigo-400">
              Astral Engine
            </span>
          </div>
          <p className="type-body-sm mt-1 text-text-secondary">
            Crie quantos planejamentos quiser para este cliente — um por lançamento, por trimestre, ou como preferir.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsTemplatesOpen(true)}
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition-all self-start sm:self-auto"
        >
          <LayoutTemplate size={14} />
          <span>Usar Modelo Pronto</span>
        </button>
      </div>

      <form onSubmit={criar} className="mt-6 flex max-w-md items-end gap-3">
        <div className="flex-1">
          <Input label="Nome do novo mapa" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Planejamento Agosto" />
        </div>
        <Button type="submit" loading={creating}>Criar</Button>
      </form>

      {maps === null && <Skeleton className="mt-6 h-24" />}

      {maps?.length === 0 && (
        <div className="mt-8">
          <EmptyState title="Nenhum mapa ainda" description="Crie o primeiro planejamento acima ou use um modelo pronto." />
        </div>
      )}

      {maps !== null && maps.length > 0 && (
        <div className="mt-6 flex flex-col gap-3">
          {maps.map((m) => {
            const dateStr = m.updated_at || m.updatedAt || new Date().toISOString();
            const LayoutIcon = m.layout === "org" ? GitFork : m.layout === "list" ? ListIcon : Network;
            return (
              <Card key={m.id}>
                {renamingId === m.id ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void salvarRenome(m.id);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      className="h-9 flex-1 rounded-[var(--radius-control-sm)] border border-border-default bg-surface-sunken px-3 text-text-primary focus:border-accent focus:outline-none"
                    />
                    <Button size="sm" onClick={() => salvarRenome(m.id)}>Salvar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)}>Cancelar</Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/clientes/${id}/mapas/${m.id}`} className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="flex h-5 w-5 items-center justify-center rounded bg-indigo-500/10 text-indigo-400">
                          <LayoutIcon size={12} />
                        </div>
                        <h2 className="type-h3 truncate text-text-cream">{m.title}</h2>
                      </div>
                      <p className="type-caption mt-1 text-text-tertiary">
                        {m.shared ? "Compartilhado · " : ""}
                        Atualizado em {new Date(dateStr).toLocaleDateString("pt-BR")}
                      </p>
                    </Link>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => {
                          setRenamingId(m.id);
                          setRenameValue(m.title);
                        }}
                      >
                        Renomear
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => excluir(m.id)}>Excluir</Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <AstralTemplatesModal
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </AdminShell>
  );
}
